import React, { useState } from 'react';
import { ChevronLeft, MapPin, Bed, Bath, Maximize, TrendingUp, Calendar, DollarSign, Users, Building2, Eye, Heart, Share2, Download, Edit, CheckCircle, Clock, Star, Phone, Mail, MessageCircle, Image as ImageIcon, FileText, BarChart3 } from 'lucide-react';
interface DeveloperProjectDetailPageProps {
  projectId: number;
  onBack: () => void;
}
export function DeveloperProjectDetailPage({
  projectId,
  onBack
}: DeveloperProjectDetailPageProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'units' | 'analytics' | 'inquiries'>('overview');
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(0);
  // Mock project data
  const project = {
    id: projectId,
    name: projectId === 1 ? 'Palm Hills October' : 'Mountain View iCity',
    location: projectId === 1 ? '6th of October City' : 'New Cairo',
    description: projectId === 1 ? 'A premium residential compound offering luxury villas and apartments with world-class amenities in the heart of 6th of October City. Experience elevated living with meticulously designed spaces, lush landscapes, and comprehensive facilities.' : 'Modern smart city development featuring innovative technology-integrated homes with sustainable living solutions in New Cairo. Embrace the future of urban living with cutting-edge smart home systems and eco-friendly infrastructure.',
    totalUnits: projectId === 1 ? 450 : 380,
    availableUnits: projectId === 1 ? 120 : 85,
    soldUnits: projectId === 1 ? 330 : 295,
    priceRange: projectId === 1 ? '2.5M - 8.5M EGP' : '3.2M - 12M EGP',
    deliveryDate: projectId === 1 ? 'Q4 2025' : 'Q2 2026',
    completionPercentage: projectId === 1 ? 75 : 60,
    totalArea: projectId === 1 ? '120 Acres' : '95 Acres',
    masterPlan: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800',
    gallery: ['https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800', 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800', 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800', 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'],
    amenities: ['Swimming Pools', 'Gym & Spa', 'Kids Area', 'Sports Club', 'Commercial Area', 'Security 24/7', 'Landscaped Gardens', 'Jogging Track', 'Clubhouse', 'Medical Center'],
    unitTypes: [{
      id: 1,
      type: 'Apartment',
      bedrooms: 2,
      bathrooms: 2,
      area: projectId === 1 ? '120 sqm' : '140 sqm',
      price: projectId === 1 ? '2.5M EGP' : '3.2M EGP',
      available: projectId === 1 ? 45 : 28,
      total: projectId === 1 ? 150 : 120,
      floorPlan: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400'
    }, {
      id: 2,
      type: 'Apartment',
      bedrooms: 3,
      bathrooms: 2,
      area: projectId === 1 ? '180 sqm' : '200 sqm',
      price: projectId === 1 ? '3.8M EGP' : '4.8M EGP',
      available: projectId === 1 ? 38 : 32,
      total: projectId === 1 ? 180 : 150,
      floorPlan: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400'
    }, {
      id: 3,
      type: projectId === 1 ? 'Villa' : 'Penthouse',
      bedrooms: 4,
      bathrooms: 3,
      area: projectId === 1 ? '350 sqm' : '300 sqm',
      price: projectId === 1 ? '6.5M EGP' : '8.5M EGP',
      available: projectId === 1 ? 22 : 15,
      total: projectId === 1 ? 80 : 60,
      floorPlan: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400'
    }, {
      id: 4,
      type: 'Villa',
      bedrooms: 5,
      bathrooms: 4,
      area: projectId === 1 ? '450 sqm' : '500 sqm',
      price: projectId === 1 ? '8.5M EGP' : '12M EGP',
      available: projectId === 1 ? 15 : 10,
      total: projectId === 1 ? 40 : 50,
      floorPlan: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400'
    }],
    stats: {
      views: projectId === 1 ? 2450 : 3120,
      inquiries: projectId === 1 ? 186 : 245,
      tours: projectId === 1 ? 94 : 128,
      favorites: projectId === 1 ? 312 : 428
    },
    recentInquiries: [{
      id: 1,
      brokerageName: 'Elite Properties Group',
      contactPerson: 'Ahmed Hassan',
      email: 'ahmed@eliteproperties.com',
      phone: '+20 123 456 7890',
      interestedIn: '3BR Apartment',
      date: '2 hours ago',
      status: 'new'
    }, {
      id: 2,
      brokerageName: 'One Broker Group',
      contactPerson: 'Sarah Mohamed',
      email: 'sarah@onebroker.com',
      phone: '+20 111 222 3333',
      interestedIn: 'Villa 4BR',
      date: '5 hours ago',
      status: 'contacted'
    }, {
      id: 3,
      brokerageName: 'Alliance Brokers Realty',
      contactPerson: 'Mohamed Ali',
      email: 'mohamed@alliance.com',
      phone: '+20 100 555 6666',
      interestedIn: '2BR Apartment',
      date: '1 day ago',
      status: 'scheduled'
    }],
    analytics: {
      monthlyViews: [{
        month: 'Jan',
        views: 1850
      }, {
        month: 'Feb',
        views: 2100
      }, {
        month: 'Mar',
        views: 2450
      }, {
        month: 'Apr',
        views: 2200
      }, {
        month: 'May',
        views: 2650
      }, {
        month: 'Jun',
        views: 3120
      }],
      topBrokerages: [{
        name: 'Elite Properties Group',
        inquiries: 45
      }, {
        name: 'One Broker Group',
        inquiries: 38
      }, {
        name: 'Alliance Brokers Realty',
        inquiries: 32
      }]
    }
  };
  const getInquiryStatusConfig = (status: string) => {
    const configs = {
      new: {
        label: 'New',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        icon: MessageCircle
      },
      contacted: {
        label: 'Contacted',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        icon: Phone
      },
      scheduled: {
        label: 'Tour Scheduled',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: Calendar
      }
    };
    return configs[status as keyof typeof configs];
  };
  return <div className="min-h-screen bg-neutral-bg pb-6 font-sans w-full" data-id="element-1745">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-1746">
        {/* Header with Image Gallery */}
        <div className="relative" data-id="element-1747">
          <div className="relative h-80" data-id="element-1748">
            <img src={project.gallery[selectedGalleryImage]} alt={project.name} className="w-full h-full object-cover" data-id="element-1749" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" data-id="element-1750"></div>

            {/* Back Button */}
            <button onClick={onBack} className="absolute top-4 left-4 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors z-10" data-id="element-1751">
              <ChevronLeft className="w-6 h-6 text-gray-700" data-id="element-1752" />
            </button>

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex gap-2 z-10" data-id="element-1753">
              <button className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors" data-id="element-1754">
                <Heart className="w-5 h-5 text-gray-700" data-id="element-1755" />
              </button>
              <button className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors" data-id="element-1756">
                <Share2 className="w-5 h-5 text-gray-700" data-id="element-1757" />
              </button>
              <button className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors" data-id="element-1758">
                <Edit className="w-5 h-5 text-gray-700" data-id="element-1759" />
              </button>
            </div>

            {/* Project Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1760">
              <h1 className="text-3xl font-bold text-white mb-2" data-id="element-1761">
                {project.name}
              </h1>
              <div className="flex items-center gap-2 text-white/90 mb-4" data-id="element-1762">
                <MapPin className="w-5 h-5" data-id="element-1763" />
                <span className="text-lg" data-id="element-1764">{project.location}</span>
              </div>
              <div className="flex items-center gap-4" data-id="element-1765">
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full" data-id="element-1766">
                  <span className="text-white font-semibold" data-id="element-1767">
                    {project.completionPercentage}% Complete
                  </span>
                </div>
                <div className="bg-accent/90 backdrop-blur-sm px-4 py-2 rounded-full" data-id="element-1768">
                  <span className="text-primary font-bold" data-id="element-1769">
                    {project.availableUnits} Units Available
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Gallery Thumbnails */}
          <div className="px-4 -mt-8 relative z-20" data-id="element-1770">
            <div className="flex gap-2 overflow-x-auto no-scrollbar" data-id="element-1771">
              {project.gallery.map((image, idx) => <button key={idx} onClick={() => setSelectedGalleryImage(idx)} className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${selectedGalleryImage === idx ? 'border-primary scale-105' : 'border-white opacity-70 hover:opacity-100'}`} data-id="element-1772">
                  <img src={image} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" data-id="element-1773" />
                </button>)}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="px-4 mt-6 mb-6" data-id="element-1774">
          <div className="grid grid-cols-4 gap-3" data-id="element-1775">
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center" data-id="element-1776">
              <Eye className="w-5 h-5 text-blue-500 mx-auto mb-1" data-id="element-1777" />
              <p className="text-lg font-bold text-neutral-text" data-id="element-1778">
                {project.stats.views}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1779">Views</p>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center" data-id="element-1780">
              <MessageCircle className="w-5 h-5 text-green-500 mx-auto mb-1" data-id="element-1781" />
              <p className="text-lg font-bold text-neutral-text" data-id="element-1782">
                {project.stats.inquiries}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1783">Inquiries</p>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center" data-id="element-1784">
              <MapPin className="w-5 h-5 text-purple-500 mx-auto mb-1" data-id="element-1785" />
              <p className="text-lg font-bold text-neutral-text" data-id="element-1786">
                {project.stats.tours}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1787">Tours</p>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center" data-id="element-1788">
              <Heart className="w-5 h-5 text-red-500 mx-auto mb-1" data-id="element-1789" />
              <p className="text-lg font-bold text-neutral-text" data-id="element-1790">
                {project.stats.favorites}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1791">Favorites</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 mb-6" data-id="element-1792">
          <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-100 flex gap-1 overflow-x-auto no-scrollbar" data-id="element-1793">
            <button onClick={() => setActiveTab('overview')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${activeTab === 'overview' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1794">
              Overview
            </button>
            <button onClick={() => setActiveTab('units')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${activeTab === 'units' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1795">
              Units
            </button>
            <button onClick={() => setActiveTab('analytics')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${activeTab === 'analytics' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1796">
              Analytics
            </button>
            <button onClick={() => setActiveTab('inquiries')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${activeTab === 'inquiries' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1797">
              Inquiries
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4 space-y-4" data-id="element-1798">
          {/* Overview Tab */}
          {activeTab === 'overview' && <>
              {/* Description */}
              <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-1799">
                <h2 className="text-base font-bold text-neutral-text mb-3" data-id="element-1800">
                  About Project
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1801">
                  {project.description}
                </p>
              </section>

              {/* Key Information */}
              <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-1802">
                <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-1803">
                  Project Details
                </h2>
                <div className="grid grid-cols-2 gap-4" data-id="element-1804">
                  <div className="flex items-center gap-3" data-id="element-1805">
                    <Building2 className="w-5 h-5 text-gray-400" data-id="element-1806" />
                    <div data-id="element-1807">
                      <p className="text-xs text-gray-500" data-id="element-1808">Total Units</p>
                      <p className="text-sm font-bold text-neutral-text" data-id="element-1809">
                        {project.totalUnits}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1810">
                    <TrendingUp className="w-5 h-5 text-gray-400" data-id="element-1811" />
                    <div data-id="element-1812">
                      <p className="text-xs text-gray-500" data-id="element-1813">Sold Units</p>
                      <p className="text-sm font-bold text-neutral-text" data-id="element-1814">
                        {project.soldUnits}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1815">
                    <DollarSign className="w-5 h-5 text-gray-400" data-id="element-1816" />
                    <div data-id="element-1817">
                      <p className="text-xs text-gray-500" data-id="element-1818">Price Range</p>
                      <p className="text-sm font-bold text-neutral-text" data-id="element-1819">
                        {project.priceRange}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1820">
                    <Calendar className="w-5 h-5 text-gray-400" data-id="element-1821" />
                    <div data-id="element-1822">
                      <p className="text-xs text-gray-500" data-id="element-1823">Delivery</p>
                      <p className="text-sm font-bold text-neutral-text" data-id="element-1824">
                        {project.deliveryDate}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1825">
                    <Maximize className="w-5 h-5 text-gray-400" data-id="element-1826" />
                    <div data-id="element-1827">
                      <p className="text-xs text-gray-500" data-id="element-1828">Total Area</p>
                      <p className="text-sm font-bold text-neutral-text" data-id="element-1829">
                        {project.totalArea}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1830">
                    <CheckCircle className="w-5 h-5 text-gray-400" data-id="element-1831" />
                    <div data-id="element-1832">
                      <p className="text-xs text-gray-500" data-id="element-1833">Completion</p>
                      <p className="text-sm font-bold text-neutral-text" data-id="element-1834">
                        {project.completionPercentage}%
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Amenities */}
              <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-1835">
                <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-1836">
                  Amenities & Facilities
                </h2>
                <div className="grid grid-cols-2 gap-3" data-id="element-1837">
                  {project.amenities.map((amenity, idx) => <div key={idx} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1838">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1839" />
                      <span data-id="element-1840">{amenity}</span>
                    </div>)}
                </div>
              </section>

              {/* Master Plan */}
              <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-1841">
                <div className="flex items-center justify-between mb-4" data-id="element-1842">
                  <h2 className="text-base font-bold text-neutral-text" data-id="element-1843">
                    Master Plan
                  </h2>
                  <button className="flex items-center gap-2 text-sm font-semibold text-secondary hover:text-secondary-dark transition-colors" data-id="element-1844">
                    <Download className="w-4 h-4" data-id="element-1845" />
                    Download
                  </button>
                </div>
                <img src={project.masterPlan} alt="Master Plan" className="w-full rounded-xl" data-id="element-1846" />
              </section>
            </>}

          {/* Units Tab */}
          {activeTab === 'units' && <div className="space-y-4" data-id="element-1847">
              {project.unitTypes.map(unit => <div key={unit.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-1848">
                  <div className="flex items-start justify-between mb-4" data-id="element-1849">
                    <div data-id="element-1850">
                      <h3 className="text-lg font-bold text-neutral-text mb-1" data-id="element-1851">
                        {unit.type}
                      </h3>
                      <p className="text-2xl font-bold text-primary" data-id="element-1852">
                        {unit.price}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-1853">
                      <p className="text-sm text-gray-500 mb-1" data-id="element-1854">Availability</p>
                      <p className="text-lg font-bold text-green-600" data-id="element-1855">
                        {unit.available}/{unit.total}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 mb-4 text-sm text-gray-600" data-id="element-1856">
                    <div className="flex items-center gap-2" data-id="element-1857">
                      <Bed className="w-5 h-5" data-id="element-1858" />
                      <span data-id="element-1859">{unit.bedrooms} Beds</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1860">
                      <Bath className="w-5 h-5" data-id="element-1861" />
                      <span data-id="element-1862">{unit.bathrooms} Baths</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1863">
                      <Maximize className="w-5 h-5" data-id="element-1864" />
                      <span data-id="element-1865">{unit.area}</span>
                    </div>
                  </div>

                  <div className="mb-4" data-id="element-1866">
                    <p className="text-sm font-semibold text-neutral-text mb-2" data-id="element-1867">
                      Floor Plan
                    </p>
                    <img src={unit.floorPlan} alt="Floor Plan" className="w-full rounded-lg" data-id="element-1868" />
                  </div>

                  <div className="flex gap-3" data-id="element-1869">
                    <button className="flex-1 bg-primary hover:bg-primary-dark text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-1870">
                      <Edit className="w-4 h-4" data-id="element-1871" />
                      Edit Unit
                    </button>
                    <button className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-1872">
                      <Download className="w-4 h-4" data-id="element-1873" />
                      Download
                    </button>
                  </div>
                </div>)}
            </div>}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && <>
              <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-1874">
                <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-1875">
                  Monthly Views Trend
                </h2>
                <div className="space-y-3" data-id="element-1876">
                  {project.analytics.monthlyViews.map((data, idx) => <div key={idx} data-id="element-1877">
                      <div className="flex items-center justify-between mb-1" data-id="element-1878">
                        <span className="text-sm font-medium text-gray-600" data-id="element-1879">
                          {data.month}
                        </span>
                        <span className="text-sm font-bold text-primary" data-id="element-1880">
                          {data.views}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1881">
                        <div className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full" style={{
                    width: `${data.views / 3500 * 100}%`
                  }} data-id="element-1882"></div>
                      </div>
                    </div>)}
                </div>
              </section>

              <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-1883">
                <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-1884">
                  Top Brokerages by Inquiries
                </h2>
                <div className="space-y-4" data-id="element-1885">
                  {project.analytics.topBrokerages.map((brokerage, idx) => <div key={idx} className="flex items-center justify-between" data-id="element-1886">
                      <div className="flex items-center gap-3" data-id="element-1887">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm" data-id="element-1888">
                          {idx + 1}
                        </div>
                        <span className="text-sm font-medium text-neutral-text" data-id="element-1889">
                          {brokerage.name}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-primary" data-id="element-1890">
                        {brokerage.inquiries} inquiries
                      </span>
                    </div>)}
                </div>
              </section>
            </>}

          {/* Inquiries Tab */}
          {activeTab === 'inquiries' && <div className="space-y-3" data-id="element-1891">
              {project.recentInquiries.map(inquiry => {
            const statusConfig = getInquiryStatusConfig(inquiry.status);
            const StatusIcon = statusConfig.icon;
            return <div key={inquiry.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-1892">
                    <div className="flex items-start justify-between mb-3" data-id="element-1893">
                      <div data-id="element-1894">
                        <h3 className="font-bold text-neutral-text mb-1" data-id="element-1895">
                          {inquiry.brokerageName}
                        </h3>
                        <p className="text-sm text-gray-600" data-id="element-1896">
                          {inquiry.contactPerson}
                        </p>
                      </div>
                      <span className={`flex items-center gap-1 ${statusConfig.bgColor} ${statusConfig.color} text-xs font-semibold px-3 py-1 rounded-full`} data-id="element-1897">
                        <StatusIcon className="w-3 h-3" data-id="element-1898" />
                        {statusConfig.label}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4" data-id="element-1899">
                      <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1900">
                        <Mail className="w-4 h-4" data-id="element-1901" />
                        <span data-id="element-1902">{inquiry.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1903">
                        <Phone className="w-4 h-4" data-id="element-1904" />
                        <span data-id="element-1905">{inquiry.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1906">
                        <Building2 className="w-4 h-4" data-id="element-1907" />
                        <span data-id="element-1908">Interested in: {inquiry.interestedIn}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-1909">
                      <span className="text-xs text-gray-400" data-id="element-1910">
                        {inquiry.date}
                      </span>
                      <button className="text-sm font-semibold text-secondary hover:text-secondary-dark transition-colors" data-id="element-1911">
                        View Details →
                      </button>
                    </div>
                  </div>;
          })}
            </div>}
        </div>
      </div>
    </div>;
}